import React, {
    MutableRefObject,
    RefObject,
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react';
import { twMerge } from 'tailwind-merge';
import { useDispatch, useSelector } from 'react-redux';
import { useIdle, useLocation, usePrevious } from 'react-use';
import { useRouter } from 'next/router';
import { FallbackProps } from 'react-error-boundary';
import dynamic from 'next/dynamic';
import cn from 'classnames';
import {
    COOKIE_KEY,
    LSTORAGE_KEY,
    PAGE_TYPE,
    SESSION_KEY,
} from 'src/constants';
import { IBreadcrumb, IFooter, ISection } from 'src/interface';
import {
    AdUtils,
    LocalStore,
    Logger,
    ObjectUtils,
    SessionStore,
    UrlUtils,
} from 'src/utils';
import { getPageShowAd } from 'src/utils/ad';
import CookieStore, { getUserZaloId } from 'src/utils/cookie';
import { trackingContentDetail } from 'src/utils/tracking';
import useAuth from 'src/hooks/useAuth';
import { isLoggedSelector } from 'src/selectors/auth';
import { isOpenSelector } from 'src/selectors/content';
import { isMobileSelector, isOnlineSelector } from 'src/selectors/app';
import { checkInstallZalo, loadAppRegion } from 'src/redux/actions/app';
import { loadAssetsUser } from 'src/redux/actions/user';
import { IAppConfig } from 'src/services/AppService';
import AuthService from 'src/services/AuthService';
import { Container, Element, Section } from 'src/components';
import { AdBanner } from 'src/modules/shared/components/ad';
import { ContentError } from 'src/modules/shared/components/Error';
import ErrorBoundary, {
    ErrorFallback,
} from 'src/modules/shared/components/ErrorBoundary';
import AdMasthead from './ui/content/adMasthead';
import Header from './header';
import Search from './header/search';
import Top from './top';
import { BreadCrumb } from './breadcrumb';
import Footer from './footer';

import styles from './layout.module.scss';

import { ZShortMasthead } from 'src/modules/event/zshort/masthead';
import { setAuthState } from 'src/redux/actions/auth/dispatchActions';

const ContainerUI = dynamic(() => import('./ui'), {
    ssr: false,
});

const TopProgressBar = dynamic(() => import('./TopProgressBar'), {
    ssr: false,
});

interface IProps {
    children: React.ReactElement;
    showMenu?: boolean;
    sectionTop?: ISection<any>[];
    breadcrumb?: IBreadcrumb;
    footer: IFooter;
    config?: IAppConfig;
    page?: string;
    prZoneIds?: string[];
    options?: any;
    showFormSearch?: boolean;
    adMasthead?: any;
    ad?: any;
    isIABM?: boolean;
    isZalo?: boolean;
    categoryIdOfPage?: number;
}

export default function LayoutApp({
    showMenu = true,
    sectionTop,
    breadcrumb,
    children,
    options = {},
    page = '',
    showFormSearch,
    ad = {},
    adMasthead = {},
    isIABM,
    isZalo,
    config,
    footer,
    categoryIdOfPage,
}: IProps) {
    const router = useRouter();
    const location = useLocation();
    const prevLocation = usePrevious(location);
    const { auth } = useAuth();
    const dispatch = useDispatch();
    const isLogged = useSelector(isLoggedSelector);
    const isOnline = useSelector(isOnlineSelector);
    const isMobile = useSelector(isMobileSelector);
    const isOpen = useSelector(isOpenSelector);
    const isPageContentDetail = UrlUtils.isContentDetailPage(page);
    const layoutRef: MutableRefObject<any> = useRef<RefObject<any>>(null);
    const { className } = options;

    const [routeError, setRouteError] = useState<null | FallbackProps>(null);

    useEffect(() => {
        if (isLogged) {
            dispatch(loadAssetsUser());
            setTimeout(() => {
                const _window: any = window;
                _window.adtimaBMConfig = {
                    ..._window.adtimaBMConfig,
                    userZaloId: getUserZaloId(),
                };
            }, 100);
        }
    }, [isLogged]);

    useEffect(() => {
        dispatch(loadAppRegion());
        checkInstallZalo();
    }, []);

    useEffect(() => {
        const oauthCode = UrlUtils.getParamUrl('code');
        const codeVerify = UrlUtils.getParamUrl('state');
        if (oauthCode && codeVerify) {
            AuthService.loginByZalo(oauthCode, codeVerify)
                .then((resp) => {
                    if (resp.data) {
                        auth();
                        LocalStore.set('refresh', JSON.stringify(true)); // lưu giá trị của form vào localStorage dưới key 'refresh'
                        LocalStore.remove('refresh');
                    }
                })
                .catch((resp) => {
                    console.log('errLogin', resp);
                })
                .finally(() => {
                    (window as any).close();
                });
        }
    }, []);

    useEffect(() => {
        window.addEventListener('message', (event) => {
            if (event.origin !== 'https://h5.zdn.vn') return;
            const { accessToken, visitorID, type } = event.data;
            if (accessToken) {
                AuthService.loginByZalo('', '', accessToken)
                    .then((resp) => {
                        if (resp.data) {
                            dispatch(setAuthState(resp.data));
                            LocalStore.set(
                                LSTORAGE_KEY.BM_UID,
                                resp.data.strUserId,
                            );
                        }
                    })
                    .catch((resp) => {
                        console.log('errLoginByAccessToken', resp);
                    });
            }

            if (visitorID) {
                CookieStore.set(COOKIE_KEY.ZA_ID, visitorID);
                LocalStore.set(COOKIE_KEY.ZA_ID, visitorID);
            }

            if (type === 'FROM_MINIAPP_GO_HOME') {
                router.replace('/');
            }
        });
    }, []);

    useEffect(() => {
        function receiveMessage(e: any) {
            if (e.key === 'refresh') {
                const message = JSON.parse(e.newValue); // chỉ check với key là 'refresh'
                if (message) {
                    setTimeout(() => {
                        (window as any).fnCallbackLogin();
                    }, 500);
                }
            }
        }
        window.addEventListener('storage', receiveMessage);
        return () => window.removeEventListener('storage', receiveMessage);
    }, []);

    const handleRefresh = (): Promise<any> => {
        return new Promise(() => {
            if (isOnline) {
                router.replace(router.asPath);
            }
        });
    };

    const intervalRef: MutableRefObject<any> = useRef(1);
    const renderAdsBottom = useCallback(() => {
        if (ad.bottoms) {
            return ad?.bottoms?.map((id: string) => {
                return <AdBanner elId={id} key={id} />;
            });
        }
        return null;
    }, []);

    const isIdleSession = useIdle(15 * 60 * 1000); // 15p
    const preIdleSession = usePrevious(isIdleSession);

    useEffect(() => {
        if (preIdleSession && !isIdleSession) {
            SessionStore.set(SESSION_KEY.SSID, new Date().getTime());
        }
    }, [isIdleSession]);

    const isIdle = useIdle(1000);
    const TIMER_REFRESH = 3 * 60 * 1000; // 3p

    useEffect(() => {
        if (isIdle) {
            if (page !== PAGE_TYPE.CONTENT_DETAIL && !isOpen)
                intervalRef.current = setInterval(() => {
                    handleRefresh();
                }, TIMER_REFRESH);
        }
        return () => {
            clearInterval(intervalRef.current);
        };
    }, [isIdle, page, isOpen, isOnline]);

    useEffect(() => {
        if (router?.asPath?.indexOf('/r/') > -1) {
            return;
        }
        AdUtils.loadScriptABP();
    }, []);

    useEffect(() => {
        (window as any).firstLoad = true;
    }, []);

    useEffect(() => {
        if (
            location.trigger === 'popstate' &&
            prevLocation?.trigger === 'pushstate' &&
            location.search
        ) {
            if (!isOpen) {
                router.replace(location.href || '');
            }
        }
    }, [location.pathname]);

    useEffect(() => {
        const handleRouteError = (err: any, url: string) => {
            if (err?.cancelled) {
                Logger.error(
                    400,
                    'Cancelled',
                    url,
                    '| pathname:',
                    location.pathname,
                    ' | err: ',
                    JSON.stringify(err),
                );
            } else {
                setRouteError(err);
            }
        };

        router.events.on('routeChangeError', handleRouteError);

        return () => {
            router.events.off('routeChangeError', handleRouteError);
        };
    }, []);

    useEffect(() => {
        const data = (LocalStore.get(LSTORAGE_KEY.VIEW_DETAIL) || {}) as any;
        if (!ObjectUtils.isEmpty(data)) {
            const value = Object.values(data)[0] as any;
            if (!ObjectUtils.isEmpty(value)) {
                trackingContentDetail(value);
                LocalStore.set(LSTORAGE_KEY.VIEW_DETAIL, {});
            }
        }
    }, []);

    const renderLayout = () => {
        return (
            <>
                <TopProgressBar />
                {adMasthead?.zoneAdaptive &&
                    isMobile &&
                    getPageShowAd(page) && (
                        <AdMasthead adMasthead={adMasthead} />
                    )}
                <div
                    className={twMerge(
                        styles['layout-app'],
                        'md:bg-[#e9ecef]',
                        cn({
                            [`page-${page}`]: page,
                            [className]: className,
                            [`page-inapp`]: isIABM,
                            ['md:bg-white md:mx-auto md:max-w-[618px]']:
                                /contentdetail/i.test(page),
                        }),
                    )}
                    ref={layoutRef}
                    id="layout"
                >
                    {!adMasthead?.zoneAdaptive && isPageContentDetail && (
                        <ZShortMasthead
                            categoryIdPage={categoryIdOfPage}
                            isZalo={isZalo}
                            ad={ad}
                        />
                    )}
                    <Header page={page} showMenu={showMenu} config={config} />

                    {routeError ? (
                        <Container>
                            <ContentError error={routeError} />
                        </Container>
                    ) : (
                        <ErrorBoundary FallbackComponent={ErrorFallback}>
                            <Element id="content-main">
                                <Section
                                    className={cn('main-container', {
                                        ['mt-[16px]']:
                                            PAGE_TYPE.HOME !== page ||
                                            !isMobile,
                                    })}
                                >
                                    {sectionTop && (
                                        <Top sections={sectionTop} />
                                    )}
                                    {showFormSearch && <Search />}
                                    <Container>
                                        {breadcrumb && (
                                            <BreadCrumb
                                                breadcrumb={breadcrumb}
                                                className={cn({
                                                    ['mb-12px']:
                                                        /contentdetail/i.test(
                                                            page,
                                                        ),
                                                })}
                                            />
                                        )}
                                        <>{children}</>
                                    </Container>
                                </Section>
                            </Element>
                            <ContainerUI page={page} />
                        </ErrorBoundary>
                    )}

                    {renderAdsBottom()}

                    <Footer footer={footer} />
                </div>
            </>
        );
    };

    return <>{renderLayout()}</>;
}
